import { useSelector } from 'react-redux';
import { useAuthTenant } from 'store/auth';
import { Box, FormControl, FormHelperText,  Button } from '@mui/material';
import { useForm } from 'react-hook-form';
import { useQueries } from 'react-query';
import { postComplianceProgram } from 'services/complice-program'
import { getFrameworks, getControlsGrouped } from 'services/gap-analysis';
import { Card } from 'Components/Card';
import ProgressStepper from 'Components/ProggessStepper';
import { Container } from 'Components/Container';
import React, { useEffect, useState } from 'react';
import FrameworkSelector from 'Components/FrameworkSelector';
import DomainControlsList from 'Components/DomainControlsList';
import { useNavigate } from 'react-router-dom';
import { PageHelper } from '../../../Components/PageHelper';

export const ComplianceProgramForm = () => {
  const selectedTenant = useSelector(useAuthTenant);
  const [selectedFrameworks, setSelectedFrameworks] = useState([]);
  const [controls, setControls] = useState([]);
  const [step, setStep] = useState(0);
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    trigger,
    unregister,
  } = useForm({
    defaultValues: {
      company_id: selectedTenant._id || '',
      type: 'company',
      frameworks: [],
    },
  });
  useEffect(() => {
    if (selectedTenant) {
      setValue('company_id', selectedTenant._id);
    }
  }, [selectedTenant, setValue]);

  const results = useQueries([
    {
      queryKey: ['frameworks'],
      queryFn: getFrameworks,
    },
  ]);

  const frameworksData = results[0].data?.data || [];

  useEffect(() => {
    const fetchControlsGrouped = async frameworksIds => {
      try {
        if (frameworksIds.length === 0) {
          setControls([]);
          return;
        }
        const result = await getControlsGrouped(frameworksIds);
        if (result.status === 200) {
          setControls(result.data);
        } else {
          console.error('Erro ao buscar controles');
        }
      } catch (error) {
        console.error('Erro ao buscar controles agrupados:', error);
      }
    };

    fetchControlsGrouped(selectedFrameworks.map(fw => fw._id));
  }, [selectedFrameworks]);

  useEffect(() => {
    if (step === 1) {
      register('frameworks', {
        validate: value => (Array.isArray(value) && value.length > 0) || 'Selecione pelo menos um framework',
      });

      if (!watch('frameworks') || watch('frameworks').length === 0) {
        setValue(
          'frameworks',
          selectedFrameworks.map(fw => fw._id),
        );
      }
    } else if (step === 0 && selectedFrameworks.length === 0) {
      unregister('frameworks');
    }
  }, [register, unregister, step, setValue, watch, selectedFrameworks]);

  const handleToggle = framework => {
    setSelectedFrameworks(prevSelected => {
      const isSelected = prevSelected.some(item => item._id === framework._id);
      const updatedSelected = isSelected
        ? prevSelected.filter(item => item._id !== framework._id)
        : [...prevSelected, framework];

      setValue(
        'frameworks',
        updatedSelected.map(fw => fw._id),
      );
      trigger('frameworks');

      return updatedSelected;
    });
  };

  const submitGapAnalysis = handleSubmit(async data => {
    try {
      const payload = {
        ...data,
        framework_ids: selectedFrameworks.map(fw => fw._id),
      };
      delete payload.frameworks;
      const response = await postComplianceProgram(payload);
      const analysisId = response.data?._id || response.data?.id;
      if (response.status === 201 || response.status === 200) {
        if (analysisId) {
          navigate(`/gap-analysis/show/${analysisId}`);
        } else {

        }

      } else {
      }
    } catch (error) {
      alert('Ocorreu um erro ao cadastrar a análise. Verifique os dados e tente novamente.');
    }
  });

  const renderStep1 = () => (
    <FormControl fullWidth margin="normal" error={!!errors.frameworks}>
      <FrameworkSelector
        frameworks={frameworksData}
        selectedFrameworks={selectedFrameworks}
        handleToggle={handleToggle}
      />
      {errors.frameworks && <FormHelperText>{errors.frameworks.message}</FormHelperText>}
    </FormControl>
  );

  const renderStep2 = () => <DomainControlsList data={controls} />;

  const renderReview = () => {
    return (
      <Box>
        <h3>Resumo do Programa de Privacidade e Proteção de Dados</h3>
        <p>
          <strong>Frameworks Selecionados:</strong>
        </p>
        {selectedFrameworks.length > 0 ? (
          <ul>
            {selectedFrameworks.map(fw => (
              <li key={fw._id}>
                {fw.sourceIdentifier} ({fw.controlCount} controles)
              </li>
            ))}
          </ul>
        ) : (
          <p>Nenhum framework selecionado</p>
        )}

        <Button variant="contained" color="primary" onClick={submitGapAnalysis}>
          Salvar Análise
        </Button>
      </Box>
    );
  };
  const stepsContent = [
    renderStep1({ frameworksData, selectedFrameworks, handleToggle }),
    renderStep2(),
    renderReview({ watch, selectedFrameworks, submitGapAnalysis }),
  ];
  const steps = ['Seleção de Frameworks', 'Revisão de Controles', 'Resumo & Confirmação'];

  // Configuração dos passos do tour interativo
  const tourSteps = [
    {
      target: 'body',
      content: 'Bem-vindo ao cadastro de Programa de Privacidade e Proteção de Dados! Este é um processo em 3 etapas onde você selecionará frameworks, revisará controles e confirmará o programa. Vamos começar!',
      placement: 'center',
      disableBeacon: true,
    },
    {
      target: '[data-tour="page-title"]',
      content: 'Clique aqui no título para voltar à lista de programas de compliance.',
      placement: 'bottom',
      title: '↩️ Voltar para Lista',
    },
    {
      target: '[data-tour="stepper"]',
      content: 'Este é o indicador de progresso. Você está na Etapa 1 de 3. O processo guiará você pela seleção de frameworks, revisão de controles e confirmação final.',
      placement: 'bottom',
      title: '📊 Progresso do Cadastro',
    },
    {
      target: '[data-tour="framework-selector"]',
      content: 'Aqui você seleciona os frameworks de compliance que deseja implementar no programa. Clique nos cards para selecionar/desselecionar. Você pode escolher múltiplos frameworks.',
      placement: 'top',
      title: '🎯 Seleção de Frameworks',
    },
    {
      target: '[data-tour="next-button"]',
      content: 'Após selecionar pelo menos um framework, clique em "Próximo" para avançar para a revisão de controles. O botão só ficará habilitado quando pelo menos um framework estiver selecionado.',
      placement: 'top',
      title: '➡️ Avançar para Próxima Etapa',
    },
    {
      target: 'body',
      content: 'Na segunda etapa, você verá todos os controles dos frameworks selecionados, organizados por domínios. Revise cada controle para entender o que será implementado.',
      placement: 'center',
      title: '📋 Etapa 2: Revisão de Controles',
    },
    {
      target: 'body',
      content: 'Na terceira e última etapa, você verá um resumo completo do programa com todos os frameworks selecionados e a quantidade total de controles. Revise e confirme para criar o programa.',
      placement: 'center',
      title: '✅ Etapa 3: Confirmação',
    },
    {
      target: 'body',
      content: 'Após salvar, o programa será criado e você será redirecionado para a página de detalhes onde poderá começar a implementar os controles. Boa sorte!',
      placement: 'center',
      title: '🎉 Finalização',
    },
    {
      target: 'body',
      content: 'Tour concluído! Você pode reativar este guia a qualquer momento clicando no botão de ajuda (?) no canto inferior direito.',
      placement: 'center',
      title: '✨ Tour Concluído!',
    },
  ];

  return (
    <Container title="Adicionar Programa de Privacidade e Proteção de Dados" breadcrumb="Gerenciar / Programa de Privacidade e Proteção de Dados" linkPage="/compliance-program">
      {/* Helper Interativo */}
      <PageHelper 
        steps={tourSteps}
        tourKey="compliance-program-form-tour"
        autoStart={true}
        showButton={true}
      />
      
      <Card sx={'mt-4'}>
        <ProgressStepper
          steps={steps}
          activeStep={step}
          onNext={async () => {
            let isValid = await trigger();
            if (step === 1) {
              const frameworksValid = await trigger('frameworks');
              isValid = isValid && frameworksValid;
            }
            if (isValid) {
              setStep(prev => prev + 1);
            }
          }}
          onBack={() => setStep(prev => prev - 1)}
          stepsContent={stepsContent}
        />
      </Card>
    </Container>
  );
};
